import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHeart, faClock, faGears, faUserShield } from '@fortawesome/free-solid-svg-icons';
import { MaintenanceStatus } from '../../services/maintenanceService';
import './MaintenanceScreen.css';

interface Props {
  status: MaintenanceStatus;
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;

function formatEta(iso: string | null, t: TFn): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return t('maintenance.todayAt', { time: date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) });
  }
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function useCountdown(targetIso: string | null): string | null {
  const target = useMemo(() => (targetIso ? new Date(targetIso).getTime() : null), [targetIso]);
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!target) return;
    // Reset `now` immediately when the target changes so the first frame of
    // the countdown reflects the new target, not a stale tick.
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);

  if (!target) return null;
  const diff = target - now;
  if (diff <= 0) return null;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes.toString().padStart(2, '0')}m`);
  parts.push(`${seconds.toString().padStart(2, '0')}s`);
  return parts.join(' ');
}

const MaintenanceScreen: React.FC<Props> = ({ status }) => {
  const { t } = useTranslation('common');
  const etaLabel = formatEta(status.expected_back_at, t);
  const countdown = useCountdown(status.expected_back_at);

  // Reflect the maintenance state in the browser tab so users browsing
  // multiple tabs notice at a glance. Restored when the screen unmounts.
  useEffect(() => {
    const previous = document.title;
    document.title = `${status.title} — InfiniCare`;
    return () => {
      document.title = previous;
    };
  }, [status.title]);

  return (
    <div className="mtn-page" role="alert" aria-live="polite">
      <div className="mtn-bg-orb mtn-bg-orb-1" aria-hidden="true" />
      <div className="mtn-bg-orb mtn-bg-orb-2" aria-hidden="true" />
      <div className="mtn-bg-orb mtn-bg-orb-3" aria-hidden="true" />

      <main className="mtn-card">
        <div className="mtn-icon-wrap">
          <div className="mtn-icon-ring" />
          <div className="mtn-icon-inner">
            <FontAwesomeIcon icon={faGears} className="mtn-gear-icon" spin />
          </div>
          <div className="mtn-icon-badge">
            <FontAwesomeIcon icon={faShieldHeart} />
          </div>
        </div>

        <h1 className="mtn-title">{status.title}</h1>
        <p className="mtn-message">{status.message}</p>

        {etaLabel && (
          <div className="mtn-eta">
            <div className="mtn-eta-row">
              <FontAwesomeIcon icon={faClock} className="mtn-eta-icon" />
              <div className="mtn-eta-text">
                <span className="mtn-eta-label">{t('maintenance.expectedBackBy')}</span>
                <span className="mtn-eta-value">{etaLabel}</span>
              </div>
            </div>
            {countdown && (
              <div className="mtn-countdown" aria-live="polite" aria-atomic="true">
                <span className="mtn-countdown-label">{t('maintenance.comingBackIn')}</span>
                <span className="mtn-countdown-value">{countdown}</span>
              </div>
            )}
          </div>
        )}

        <div className="mtn-status-pill">
          <span className="mtn-status-dot" />
          <span>{t('maintenance.systemsUpdating')}</span>
        </div>

        <footer className="mtn-footer">
          <Link to="/login?bypass=admin" className="mtn-admin-link">
            <FontAwesomeIcon icon={faUserShield} />
            <span>{t('maintenance.adminSignIn')}</span>
          </Link>
          <span className="mtn-brand">InfiniCare</span>
        </footer>
      </main>
    </div>
  );
};

export default MaintenanceScreen;
